import { useCallback, useMemo, type ReactNode } from 'react';
import {
  useProposal,
  useUpdateProposals,
} from '@features/proposals/hooks/use-proposals.hooks';
import { getProject, useUpdateProject } from '@client/store/project';
import { getSelectedVersion, useSelectedVersion } from '@client/store/ui';
import { updateBuildingVersionProperties } from '@shared/helpers/project.helpers';
import { ReportEditablePageHeader as EditablePageHeader } from '../EditablePageHeader';
import {
  buildReportPageDescriptionUpdate,
  buildReportPageTitleUpdate,
  getReportPageDescription,
  getReportPageTitle,
  type ReportPageHeaderId,
} from '../../report-page-header.helpers';
import type { IProposal } from '@shared/models/proposals.model';

const BODY_PLACEHOLDER = 'Add a short description for this proposal\u2026';

type PageHeaderBlockProps = {
  /** Which report slide this header belongs to — controls persisted fields. */
  pageId: ReportPageHeaderId;
  /** Fallback title (version or proposal name) shown as placeholder when unset. */
  defaultTitle: string;
  /** Scenario id (version id for baseline, proposal id otherwise). */
  proposalId: IProposal['id'];
  isBaseline?: boolean;
  bodyPlaceholder?: string;
  /**
   * When true, print/PDF shows {@link bodyPlaceholder} if the body is empty.
   * Default omits editor prompts such as "Add a short description…".
   */
  showPlaceholderInPrint?: boolean;
  /** Figma page-edge offsets. Off when {@link Report.Page} already applies padding. */
  edgeInset?: boolean;
  /** Small label after the title, e.g. `1/2` when a slide is split. */
  titleSuffix?: ReactNode;
};

/** Per-slide report header — wires {@link ReportEditablePageHeader} to proposal/version storage. */
export const PageHeaderBlock: React.FC<PageHeaderBlockProps> = ({
  pageId,
  defaultTitle,
  proposalId,
  isBaseline = false,
  bodyPlaceholder = BODY_PLACEHOLDER,
  showPlaceholderInPrint = false,
  edgeInset = true,
  titleSuffix,
}) => {
  const updateProposals = useUpdateProposals();
  const updateProject = useUpdateProject();
  const proposal = useProposal(proposalId);
  const version = useSelectedVersion();
  const source = isBaseline ? version : proposal;
  const titleText = source ? getReportPageTitle(source, pageId) : undefined;
  const bodyText = source
    ? getReportPageDescription(source, pageId)
    : undefined;

  const handleSaveTitle = useCallback(
    (next: string) => {
      const titleUpdate = buildReportPageTitleUpdate(pageId, next);
      if (isBaseline) {
        const selectedVersion = getSelectedVersion();
        if (!selectedVersion) return;
        // TODO(replay): express as ProjectChange so a 422 race can be replayed.
        updateProject({
          kind: 'snapshot',
          project: updateBuildingVersionProperties(getProject(), {
            id: selectedVersion.id,
            ...titleUpdate,
          }),
        });
        return;
      }

      void updateProposals({
        id: proposalId,
        ...titleUpdate,
      });
    },
    [isBaseline, pageId, proposalId, updateProject, updateProposals],
  );

  const handleSaveBody = useCallback(
    (next: string) => {
      const descriptionUpdate = buildReportPageDescriptionUpdate(pageId, next);
      if (isBaseline) {
        const selectedVersion = getSelectedVersion();
        if (!selectedVersion) return;
        // TODO(replay): express as ProjectChange so a 422 race can be replayed.
        updateProject({
          kind: 'snapshot',
          project: updateBuildingVersionProperties(getProject(), {
            id: selectedVersion.id,
            ...descriptionUpdate,
          }),
        });
        return;
      }

      void updateProposals({
        id: proposalId,
        ...descriptionUpdate,
      });
    },
    [isBaseline, pageId, proposalId, updateProject, updateProposals],
  );

  const bodyFields = useMemo(
    () => [
      {
        id: 'body',
        text: bodyText,
        placeholder: bodyPlaceholder,
        showPlaceholderInPrint,
        onSave: handleSaveBody,
      },
    ],
    [bodyText, bodyPlaceholder, handleSaveBody, showPlaceholderInPrint],
  );

  return (
    <EditablePageHeader
      resetKey={`${proposalId}:${pageId}`}
      defaultTitle={defaultTitle}
      titleText={titleText}
      onSaveTitle={handleSaveTitle}
      bodyFields={bodyFields}
      titleFullWidth
      edgeInset={edgeInset}
      titleSuffix={titleSuffix}
    />
  );
};
